import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FinancialMetrics {
  // KPIs principales
  totalRevenue: number;
  maxPayment: number;
  minPayment: number;
  revenuePerUser: number;
  delinquencyRate: number;
  monthlyProjection: number;
  recoverableRevenue: number;
  
  // Distribución por plan
  revenueByPlan: {
    planDays: number;
    label: string;
    revenue: number;
    count: number;
    avgTicket: number;
  }[];
  
  // Distribución por rango de monto
  paymentDistribution: {
    range: string;
    count: number;
    total: number;
  }[];
  
  // Comparativa trimestral
  quarterlyComparison: {
    quarter: string;
    revenue: number;
    payments: number;
  }[];
}

export const useGymFinancialMetrics = (gymId: string) => {
  return useQuery({
    queryKey: ["gym-financial-metrics", gymId],
    queryFn: async (): Promise<FinancialMetrics> => {
      // Get all users for this gym
      const { data: users } = await supabase
        .from("profiles")
        .select("id")
        .eq("gym_id", gymId);

      const userIds = users?.map(u => u.id) || [];

      if (userIds.length === 0) {
        return getEmptyMetrics();
      }

      // Get all payments for these users
      const { data: payments } = await supabase
        .from("payments")
        .select("*")
        .in("user_id", userIds)
        .order("paid_at", { ascending: false });

      if (!payments || payments.length === 0) {
        return getEmptyMetrics();
      }

      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      // Calculate total revenue
      const totalRevenue = payments.reduce((sum, p) => sum + Number(p.amount), 0);

      // Max and min payments
      const amounts = payments.map(p => Number(p.amount));
      const maxPayment = Math.max(...amounts);
      const minPayment = Math.min(...amounts);

      // Revenue per user (unique paying users)
      const uniquePayingUsers = new Set(payments.map(p => p.user_id)).size;
      const revenuePerUser = uniquePayingUsers > 0 ? totalRevenue / uniquePayingUsers : 0;

      // Delinquency rate - users with expired memberships vs active
      const activeUsers = users?.filter(u => {
        const userPayments = payments.filter(p => p.user_id === u.id);
        if (userPayments.length === 0) return false;
        
        const latestPayment = userPayments[0];
        const paidAt = new Date(latestPayment.paid_at || latestPayment.created_at || now);
        const expiresAt = new Date(paidAt);
        expiresAt.setDate(expiresAt.getDate() + (latestPayment.validity_days || 30));
        
        return expiresAt > now;
      }) || [];

      const delinquencyRate = userIds.length > 0 
        ? ((userIds.length - activeUsers.length) / userIds.length) * 100 
        : 0;

      // Monthly projection based on current month trend
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      const currentDay = now.getDate();
      const currentMonthPayments = payments.filter(p => {
        const paidAt = new Date(p.paid_at || p.created_at || now);
        return paidAt.getMonth() === currentMonth && paidAt.getFullYear() === currentYear;
      });
      const currentMonthRevenue = currentMonthPayments.reduce((sum, p) => sum + Number(p.amount), 0);
      const monthlyProjection = currentDay > 0 ? (currentMonthRevenue / currentDay) * daysInMonth : 0;

      // Recoverable revenue - from expiring memberships
      const expiringIn30Days = users?.filter(u => {
        const userPayments = payments.filter(p => p.user_id === u.id);
        if (userPayments.length === 0) return false;
        
        const latestPayment = userPayments[0];
        const paidAt = new Date(latestPayment.paid_at || latestPayment.created_at || now);
        const expiresAt = new Date(paidAt);
        expiresAt.setDate(expiresAt.getDate() + (latestPayment.validity_days || 30));
        
        const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
      }) || [];

      const avgPayment = totalRevenue / payments.length;
      const recoverableRevenue = expiringIn30Days.length * avgPayment;

      // Revenue by plan type
      const planGroups: Record<number, { revenue: number; count: number }> = {};
      payments.forEach(p => {
        const days = p.validity_days || 30;
        if (!planGroups[days]) {
          planGroups[days] = { revenue: 0, count: 0 };
        }
        planGroups[days].revenue += Number(p.amount);
        planGroups[days].count += 1;
      });

      const revenueByPlan = Object.entries(planGroups).map(([days, data]) => ({
        planDays: Number(days),
        label: getPlanLabel(Number(days)),
        revenue: data.revenue,
        count: data.count,
        avgTicket: data.revenue / data.count,
      })).sort((a, b) => a.planDays - b.planDays);

      // Payment distribution by amount ranges
      const ranges = [
        { min: 0, max: 5000, label: "$0 - $5k" },
        { min: 5000, max: 10000, label: "$5k - $10k" },
        { min: 10000, max: 20000, label: "$10k - $20k" },
        { min: 20000, max: 50000, label: "$20k - $50k" },
        { min: 50000, max: Infinity, label: "$50k+" },
      ];

      const paymentDistribution = ranges.map(range => {
        const inRange = payments.filter(p => Number(p.amount) >= range.min && Number(p.amount) < range.max);
        return {
          range: range.label,
          count: inRange.length,
          total: inRange.reduce((sum, p) => sum + Number(p.amount), 0),
        };
      }).filter(r => r.count > 0);

      // Quarterly comparison (last 4 quarters)
      const quarterlyComparison = [];
      for (let i = 0; i < 4; i++) {
        const quarterEnd = new Date(currentYear, currentMonth - (i * 3), 0);
        const quarterStart = new Date(quarterEnd.getFullYear(), quarterEnd.getMonth() - 2, 1);
        
        const quarterPayments = payments.filter(p => {
          const paidAt = new Date(p.paid_at || p.created_at || now);
          return paidAt >= quarterStart && paidAt <= quarterEnd;
        });

        const quarterNames = ["Q1", "Q2", "Q3", "Q4"];
        const quarterIndex = Math.floor(quarterStart.getMonth() / 3);
        
        quarterlyComparison.unshift({
          quarter: `${quarterNames[quarterIndex]} ${quarterStart.getFullYear()}`,
          revenue: quarterPayments.reduce((sum, p) => sum + Number(p.amount), 0),
          payments: quarterPayments.length,
        });
      }

      return {
        totalRevenue,
        maxPayment,
        minPayment,
        revenuePerUser,
        delinquencyRate,
        monthlyProjection,
        recoverableRevenue,
        revenueByPlan,
        paymentDistribution,
        quarterlyComparison,
      };
    },
    enabled: !!gymId,
  });
};

function getPlanLabel(days: number): string {
  if (days <= 7) return "Semanal";
  if (days <= 15) return "Quincenal";
  if (days <= 31) return "Mensual";
  if (days <= 62) return "Bimestral";
  if (days <= 93) return "Trimestral";
  if (days <= 186) return "Semestral";
  return "Anual";
}

function getEmptyMetrics(): FinancialMetrics {
  return {
    totalRevenue: 0,
    maxPayment: 0,
    minPayment: 0,
    revenuePerUser: 0,
    delinquencyRate: 0,
    monthlyProjection: 0,
    recoverableRevenue: 0,
    revenueByPlan: [],
    paymentDistribution: [],
    quarterlyComparison: [],
  };
}
